// Built by pipeline/build-theme.mjs — edit theme-src/wig-builder.src.js and pipeline/pricing.mjs, not this file.
(() => {
// Schwan wig builder: the single source of pricing rules.
// Used by the storefront (theme asset), the QA script and the order checker, so all three always agree.
//
// Wig price = bundles at the shop's own bundle price (same texture + length)
//           + the closure or frontal at the shop's own price (same texture, lace up to 24")
//           + the wig making fee for that wig type (same at every length)
//           then rounded UP, so a wig is never cheaper than its parts.
const LENGTHS = [14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 40];
const DENSITIES = [3, 4, 5, 6, 7, 8];               // bundles (100g each); 7 & 8 only unlock past 32"
const CAPS = ['Small', 'Medium', 'Large'];
const TYPES = {
  'flip-over': { label: 'Flip over wig', lace: null, fee: 'feeFlip', lengths: [18, 24] },   // made from 18" to 24" only
  'closure': { label: 'Closure wig', lace: 'closure', fee: 'feeClosure' },
  'frontal': { label: 'Frontal wig', lace: 'frontal', fee: 'feeFrontal' },
};
const MAX_LACE = 24;                                 // closures and frontals are made up to 24"
const LACE_LENGTHS = [14, 16, 18, 20, 22, 24];       // the customer's free choice, independent of the weft length

// Optional customisation, added after cap size. Multi-select, each a flat add-on at any length.
// lace: true means the option only exists on wigs that have a closure or frontal.
// A flip-over wig has no lace, so it has no hairline to pluck and no knots to bleach.
const CUSTOM = {
  glueless: { label: 'Glueless',                price: 10, lace: true,  note: 'Adjustable band and combs fitted, so the wig holds without adhesive.' },
  hairline: { label: 'Hairline customisation', price: 60, lace: true,  note: 'We email you after you order for a photo of your own hairline, then pluck and shape to match it.' },
  knots:    { label: 'Bleached knots',         price: 60, lace: true,  note: 'Knots lightened so the lace disappears against your scalp.' },
  cut:      { label: 'Cut and layers',         price: 80, lace: false, note: 'Face-framing layers or a blunt cut, shaped once the wig is built.' },
  colour:   { label: 'Colour, jet black',      price: 80, lace: false, note: 'Dyed jet black with the lace protected. Add a few days to dispatch.' },
};
// which options apply to this build (bleached knots need lace)
const customKeys = cfg => Object.keys(CUSTOM).filter(k => !CUSTOM[k].lace || !!TYPES[cfg.type]?.lace);
// catalogue order, not click order, so notes and the order summary match the on-screen chips
const customChosen = cfg => customKeys(cfg).filter(k => (cfg.custom || []).includes(k));
const customTotal = cfg => customChosen(cfg).reduce((sum, k) => sum + CUSTOM[k].price, 0);
// Stock never blocks a build: wigs are made to order, so sold-out pieces can still be chosen.
// Bundle guidance by length (shop's own recommendation):
// 14-16" = 3 · 18-24" = 3-4 · 26-32" = 4-6 · over 32" = 6-8 (600-800g)
const minBundles = length => (length >= 34 ? 6 : length >= 26 ? 4 : 3);
const maxBundles = length => (length >= 34 ? 8 : length >= 26 ? 6 : length >= 18 ? 4 : 3);
// Closure/frontal tables carry a size dimension now:
//   { sizes:[ '5x5"','6x6"','7x7"' ], byLen:{ [size]:{ [len]:{price,available} } } }
// Single-size pieces (frontals, un-split closures) have exactly one entry in sizes.
const laceSizeOf = (cfg, lt) => cfg.laceSize || (lt && lt.sizes && lt.sizes[0]) || null;

// default suggestion: the longest in-stock closure/frontal that isn't longer than the wig
function suggestLace(cfg, table) {
  const part = TYPES[cfg.type]?.lace; if (!part) return null;
  const lt = table?.[cfg.texture]?.[part]; if (!lt || !lt.byLen) return null;
  const byLen = lt.byLen[laceSizeOf(cfg, lt)]; if (!byLen) return null;
  const fit = Object.entries(byLen).filter(([L, v]) => +L <= Math.min(cfg.length, MAX_LACE) && v.available).map(([L]) => +L);
  return fit.length ? Math.max(...fit) : null;
}

// table[texture] = { weft: { [len]: { price, available } },
//                    closure/frontal: { sizes:[...], byLen:{ [size]:{ [len]:{price,available} } } } }
// settings = { feeFlip, feeClosure, feeFrontal, roundTo, adjust }
function check(cfg, table) {
  const t = table[cfg.texture];
  if (!t) return 'Choose a texture';
  if (!TYPES[cfg.type]) return 'Choose a wig type';
  if (!LENGTHS.includes(cfg.length)) return 'Choose a length';
  const range = TYPES[cfg.type].lengths;
  if (range && (cfg.length < range[0] || cfg.length > range[1])) return `${TYPES[cfg.type].label}s are made from ${range[0]}" to ${range[1]}"`;
  const w = t.weft?.[cfg.length];
  if (!w) return `${cfg.length}" isn't available in this texture`;
  const part = TYPES[cfg.type].lace;
  if (part) {
    const lt = t[part];
    if (!lt || !lt.sizes || !lt.sizes.length) return `No ${part} in this texture`;
    if (lt.sizes.length > 1 && !cfg.laceSize) return `Choose your ${part} size`;
    const size = laceSizeOf(cfg, lt);
    if (!lt.byLen[size]) return `Choose your ${part} size`;
    if (!cfg.lace) return `Choose your ${part} length`;
    if (!LACE_LENGTHS.includes(cfg.lace)) return `${part[0].toUpperCase() + part.slice(1)}s are made from 14" to 24"`;
    if (!lt.byLen[size][cfg.lace]) return `${cfg.lace}" ${part}s aren't made in this size`;
  }
  if (!DENSITIES.includes(cfg.bundles)) return 'Choose a density';
  if (cfg.bundles < minBundles(cfg.length)) return cfg.length >= 34 ? 'Over 32" we use at least 6 bundles (600g)' : 'From 26" we use at least 4 bundles for a full look';
  if (cfg.bundles > maxBundles(cfg.length)) return `For ${cfg.length}" we recommend ${minBundles(cfg.length) * 100}–${maxBundles(cfg.length) * 100}g`;
  if (!CAPS.includes(cfg.cap)) return 'Choose a cap size';
  return null;
}
function price(cfg, table, s) {
  const err = check(cfg, table); if (err) return { error: err };
  const t = table[cfg.texture];
  const hair = cfg.bundles * t.weft[cfg.length].price;
  const part = TYPES[cfg.type].lace;
  let lace = 0, laceLen = null, laceSize = null;
  if (part) { const lt = t[part]; laceSize = laceSizeOf(cfg, lt); laceLen = cfg.lace; lace = lt.byLen[laceSize][laceLen].price; }
  const fee = +s[TYPES[cfg.type].fee];
  const parts = (hair + lace) * (+s.adjust || 1);
  const extras = customTotal(cfg);                   // optional customisation, never discounted by adjust
  const step = +s.roundTo || 10;
  const total = Math.ceil((parts + fee + extras - 1e-9) / step) * step;
  return { total, hair, lace, laceLen, laceSize, fee, parts, extras };
}

// Schwan - Build your own wig (storefront). Pricing rules live in wig-builder-pricing.js, shared with the QA script and the order checker.

const root = document.querySelector('[data-wb]');
if (root) {
  // a refresh clears the choices, so always start at the top (phones otherwise restore the old scroll position)
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  const toTop = () => { if (!location.hash) window.scrollTo(0, 0); };
  toTop();
  addEventListener('load', () => requestAnimationFrame(toTop));
  addEventListener('pageshow', e => { if (e.persisted) toTop(); });
  const data = JSON.parse(document.getElementById('wb-data-' + root.dataset.sectionId).textContent);
  data.index = data.index || {};
  const S = data.settings;
  const byHandle = Object.fromEntries(data.products.map(p => [p.h, p]));
  const lengthOf = opt => parseInt(String(opt).replace(/[^0-9]/g, ''), 10);
  // variants are [option1, option2, price, available]; find which option holds the length
  const lenIdx = p => { const i = (p.on || []).findIndex(n => /length/i.test(n)); return i >= 0 ? i : 0; };
  const weftTable = handle => {
    const p = byHandle[handle]; const out = {};
    if (p) { const li = lenIdx(p); for (const row of p.v) { const L = lengthOf(row[li]); if (L) out[L] = { price: +row[2], available: !!row[3] }; } }
    return out;
  };
  // closures/frontals: { sizes:['5x5"',...], byLen:{ [size]:{ [len]:{price,available} } }, material }
  const laceTable = handle => {
    const p = byHandle[handle]; if (!p) return { sizes: [], byLen: {}, material: 'HD' };
    const li = lenIdx(p); const si = (p.on || []).findIndex((n, i) => i !== li);
    const titleSize = (p.t.match(/\d+x\d+/) || [''])[0];
    const byLen = {}; const set = new Set();
    for (const row of p.v) { const L = lengthOf(row[li]); if (!L) continue; const size = si >= 0 ? row[si] : (titleSize ? titleSize + '"' : '-'); set.add(size); (byLen[size] ??= {})[L] = { price: +row[2], available: !!row[3] }; }
    const sizes = [...set].sort((a, b) => lengthOf(a) - lengthOf(b));
    return { sizes, byLen, material: /swiss/i.test(p.t) ? 'Swiss' : 'HD' };
  };
  // live prices and stock from the shop's own bundle, closure and frontal products
  const table = {};
  for (const t of data.textures) {
    table[t.key] = { weft: weftTable(t.weft), closure: laceTable(t.closure), frontal: laceTable(t.frontal) };
    t.image = byHandle[t.weft]?.i || null;
    t.soldOut = !Object.values(table[t.key].weft).some(v => v.available);
  }

  const cfg = { texture: null, type: null, laceSize: null, length: null, lace: null, bundles: null, cap: null, custom: [] };
  // lace label reflects the chosen size, e.g. '6x6" HD lace'
  const laceLabel = part => {
    const lt = table[cfg.texture]?.[part]; if (!lt || !lt.sizes.length) return '';
    return `${cfg.laceSize || lt.sizes[0]} ${lt.material} lace`;
  };
  // only show what the shop actually lists (e.g. no 40" until 40" wefts exist)
  const offeredWefts = () => {
    const src = cfg.texture ? [table[cfg.texture]] : Object.values(table);
    let ls = [...new Set(src.flatMap(t => Object.keys(t.weft).map(Number)))].filter(L => LENGTHS.includes(L)).sort((a, b) => a - b);
    const range = TYPES[cfg.type]?.lengths;
    if (range) ls = ls.filter(L => L >= range[0] && L <= range[1]);
    return ls;
  };
  const offeredLace = part => { const lt = table[cfg.texture]?.[part]; const byLen = lt && lt.byLen[cfg.laceSize || lt.sizes[0]]; return byLen ? LACE_LENGTHS.filter(L => byLen[L]) : []; };
  const $ = s => root.querySelector(s);
  const money = n => '£' + Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 });
  const tex = () => data.textures.find(t => t.key === cfg.texture);
  const probe = patch => {
    const c = { ...cfg, ...patch };
    const L = c.length || 20;
    const type = c.type || 'flip-over', part = TYPES[type].lace;
    const lt = part ? table[c.texture]?.[part] : null;
    const laceSize = c.laceSize || (lt?.sizes[0] || null);
    let laceL = c.lace || null;
    if (part && !laceL) { const byLen = lt?.byLen[laceSize]; if (byLen) laceL = LACE_LENGTHS.find(x => byLen[x]) || null; }
    return price({ texture: c.texture, type, laceSize, length: L, lace: laceL, bundles: c.bundles && c.bundles >= minBundles(L) ? c.bundles : minBundles(L), cap: c.cap || 'Medium' }, table, S);
  };
  const NOTES = { 'flip-over': 'Bundles only, no lace. Flip your own hair over for the most natural blend.', closure: 'Bundles plus a lace closure at the parting. Low maintenance.', frontal: 'Bundles plus a lace frontal from ear to ear, for styling versatility.' };

  function render() {
    const ty = TYPES[cfg.type], part = ty?.lace;
    const steps = [], soldNotes = {};
    let n = 0, open = true;
    const add = (key, title, hint, done, body) => { n++; steps.push({ key, done, unlocked: open, html: `<div class="wb-step-head"><span class="wb-step-num">${n}</span><h2 class="wb-step-title">${title}</h2></div><p class="wb-step-hint">${hint}</p>${body}` }); open = open && done; };

    add('texture', 'Texture', 'All raw, single-donor hair from our Indian, Vietnamese, Cambodian and Burmese collections.', !!cfg.texture, `
      <div class="wb-tex">${data.textures.map(t => `
        <button type="button" data-set="texture" data-val="${t.key}">
          ${t.image ? `<img src="${t.image}" alt="" loading="lazy">` : '<img alt="">'}
          <span>${t.label.replace('Raw ', '')}<small>${t.drawn || (t.collection + ' collection')}</small></span>
        </button>`).join('')}</div>`);

    add('style', 'Style', 'Every wig starts with at least 3 bundles.', !!cfg.type, `
      <div class="wb-styles">${Object.entries(TYPES).map(([k, v]) => {
        const lt = v.lace && cfg.texture ? table[cfg.texture]?.[v.lace] : null;
        const lab = lt && lt.sizes.length ? (v.lace === 'closure' ? lt.material + ' lace' : lt.sizes[0] + ' ' + lt.material + ' lace') : null;
        const r = cfg.texture ? probe({ type: k, length: cfg.length && (!v.lengths || (cfg.length >= v.lengths[0] && cfg.length <= v.lengths[1])) ? cfg.length : (v.lengths ? v.lengths[0] : 14), lace: null }) : {};
        const off = cfg.texture && v.lace && !lab;
        return `<button type="button" class="wb-style" data-set="type" data-val="${k}" ${off ? 'disabled' : ''}><b>${v.label}</b><small>${NOTES[k]}${lab ? ' ' + lab + '.' : ''}${r.total && !cfg.length ? ' From ' + money(r.total) + '.' : ''}</small></button>`;
      }).join('')}</div>`);

    // For lace wigs the lace size + length come before the weft length (the piece defines the wig)
    if (part) {
      const name = part === 'closure' ? 'Closure' : 'Frontal';
      const lt = table[cfg.texture]?.[part];
      if (lt && lt.sizes.length && (part === 'closure' || lt.sizes.length > 1)) {
        add('laceSize', `${name} size`, 'The lace area at your parting. A bigger size gives more parting and styling room.', !!cfg.laceSize, `
          <div class="wb-chips">${lt.sizes.map(sz => `<button type="button" class="wb-chip" data-set="laceSize" data-val="${sz.replace(/"/g, '&quot;')}">${sz}</button>`).join('')}</div>`);
      }
      const sizeReady = !lt || lt.sizes.length <= 1 || !!cfg.laceSize;
      add('lace', `${name} length`, `${name}s are made from 14" to 24". Choose any length; it doesn't have to match your wefts, and a shorter ${part} costs less.`, !!cfg.lace,
        sizeReady ? `<div class="wb-chips">${offeredLace(part).map(L => {
          const v = lt.byLen[cfg.laceSize || lt.sizes[0]][L];
          return `<button type="button" class="wb-chip" data-set="lace" data-val="${L}">${L}"${v ? ` <small>${money(v.price * (+S.adjust || 1))}</small>` : ''}</button>`;
        }).join('')}</div>` : `<p class="wb-msg">Choose your ${name.toLowerCase()} size first.</p>`);
    }

    add('weft', 'Weft length', 'Measured straight. Curly and wavy textures look shorter until stretched.' , !!cfg.length, `
      <div class="wb-chips">${offeredWefts().map(L => {
        const r = cfg.texture ? probe({ length: L }) : {};
        const why = r.error && !/full look|6 bundles/.test(r.error) ? r.error : '';
        if (why && /sold out|in stock/.test(why)) (soldNotes[why] ??= []).push(L + '"');
        return `<button type="button" class="wb-chip" data-set="length" data-val="${L}" ${why ? 'disabled title="' + why.replace(/"/g, '&quot;') + '"' : ''}>${L}"</button>`;
      }).join('')}</div>${Object.entries(soldNotes).map(([why, ls]) => `<p class="wb-msg">${ls.join(', ')}: ${why.replace(/^\d+" /, '')}.</p>`).join('')}`);

    add('density', 'Density', 'Each bundle is 100g. Longer lengths need more hair for a full look.', !!cfg.bundles, `
      <div class="wb-chips">${DENSITIES.filter(b => b >= minBundles(cfg.length || 20) && b <= maxBundles(cfg.length || 20)).map(b => {
        const r = cfg.length && (!part || cfg.lace) ? price({ ...cfg, bundles: b, cap: cfg.cap || 'Medium' }, table, S) : {};
        return `<button type="button" class="wb-chip" data-set="bundles" data-val="${b}" ${r.error ? 'disabled title="' + r.error.replace(/"/g, '&quot;') + '"' : ''}>${b * 100}g <small>${b} bundles${r.total ? ' · ' + money(r.total) : ''}</small></button>`;
      }).join('')}</div>`);

    add('cap', 'Cap size', 'Small 21"+, Medium 22"+, Large 23"+. Measure around your head above the ears and eyebrows.', !!cfg.cap, `
      <div class="wb-chips">${CAPS.map(c => `<button type="button" class="wb-chip" data-set="cap" data-val="${c}">${c}</button>`).join('')}</div>`);

    // optional, so it never blocks the build: skipping it still gives a wig that is ready to wear
    add('custom', 'Customisation', 'Optional. Pick as many as you like, or skip it and your wig still arrives washed, conditioned and ready to wear.', cfg.custom.length > 0, `
      <div class="wb-chips">${customKeys(cfg).map(k => {
        const o = CUSTOM[k];
        return `<button type="button" class="wb-chip wb-chip--multi" data-toggle="${k}" aria-pressed="${cfg.custom.includes(k)}">${o.label} <small>+${money(o.price)}</small></button>`;
      }).join('')}</div>
      ${customChosen(cfg).length ? `<ul class="wb-custom-notes">${customChosen(cfg).map(k => `<li>${CUSTOM[k].note}</li>`).join('')}</ul>` : ''}`);

    const host = $('[data-wb-steps]');
    steps.forEach((st, i) => {
      let el = host.children[i];
      if (!el || el.dataset.key !== st.key) {                       // a step appeared or disappeared (closure/frontal length)
        el = document.createElement('div'); el.dataset.key = st.key; el.dataset.html = '';
        host.children[i] ? host.replaceChild(el, host.children[i]) : host.appendChild(el);
      }
      el.className = 'wb-step' + (st.done ? ' is-done' : '') + (st.unlocked ? '' : ' is-locked');
      if (el.dataset.html !== st.html) { el.innerHTML = st.html; el.dataset.html = st.html; }   // only the parts that changed
    });
    while (host.children.length > steps.length) host.lastElementChild.remove();
    swipeDots();
    // selection state lives on the buttons, so choosing a texture never rebuilds the photos
    root.querySelectorAll('[data-set]').forEach(b => b.setAttribute('aria-pressed', String(cfg[b.dataset.set] == b.dataset.val)));
    summary();
  }

  // dots under the texture row on phones (one set only, reused on every redraw)
  function swipeDots() {
    const row = $('.wb-tex'); if (!row) return;
    const step = row.closest('.wb-step'); if (!step) return;
    const all = step.querySelectorAll('.wb-dots');
    all.forEach((d, i) => { if (i > 0) d.remove(); });
    let dots = all[0] || null;
    const pages = Math.ceil(row.scrollWidth / row.clientWidth);
    const mark = () => {
      if (!dots) return;
      const i = Math.min(pages - 1, Math.round(row.scrollLeft / row.clientWidth));
      [...dots.children].forEach((c, k) => c.classList.toggle('on', k === i));
    };
    if (pages < 2) { dots?.remove(); return; }
    if (!dots) { dots = document.createElement('div'); dots.className = 'wb-dots'; row.after(dots); }
    if (dots.children.length !== pages) dots.innerHTML = '<i></i>'.repeat(pages);
    if (!row.dataset.wired) {
      row.dataset.wired = '1';
      row.addEventListener('scroll', () => mark(), { passive: true });
      addEventListener('resize', () => swipeDots());
    }
    mark();
  }

  function summary() {
    const t = tex(), ty = TYPES[cfg.type];
    const lines = [
      ['Texture', t ? t.label.replace('Raw ', '') : '–'],
      ['Style', ty ? ty.label + (ty.lace ? ', ' + laceLabel(ty.lace) : '') : '–'],
      ['Weft length', cfg.length ? cfg.length + '"' : '–'],
      ...(ty?.lace ? [[ty.lace === 'closure' ? 'Closure' : 'Frontal', (cfg.laceSize ? cfg.laceSize + ', ' : '') + (cfg.lace ? cfg.lace + '"' : '–')]] : []),
      ['Density', cfg.bundles ? cfg.bundles * 100 + 'g (' + cfg.bundles + ' bundles)' : '–'],
      ['Cap size', cfg.cap || '–'],
      ...(customChosen(cfg).length ? [['Customisation', customChosen(cfg).map(k => CUSTOM[k].label).join(', ')]] : []),
    ];
    $('[data-wb-list]').innerHTML = lines.map(([k, v]) => `<li><span>${k}</span><b>${v}</b></li>`).join('');
    const r = price({ ...cfg, cap: cfg.cap || 'Medium' }, table, S);
    const full = price(cfg, table, S);
    const variant = !full.error ? data.index[String(full.total)] : null;
    $('[data-wb-break]').innerHTML = r.error ? '' : [
      [`${cfg.bundles} bundles × ${cfg.length}"`, r.hair],
      ...(ty.lace ? [[`${laceLabel(ty.lace)} at ${cfg.lace}"`, r.lace]] : []),
      ['Washing, conditioning and construction', r.fee],
      ...customChosen(cfg).map(k => [CUSTOM[k].label, CUSTOM[k].price]),
    ].map(([k, v]) => `<li><span>${k}</span><b>${money(v)}</b></li>`).join('');
    root.querySelectorAll('[data-wb-price],[data-wb-bar-price]').forEach(el => el.textContent = r.error ? '–' : money(r.total));
    $('[data-wb-note]').textContent = full.error
      ? (!r.error ? 'Choose your cap size to add it to your bag.' : (cfg.length && cfg.bundles ? r.error : ''))
      : (variant ? '' : 'This build needs a quote. Please message us.');
    root.querySelectorAll('[data-wb-add]').forEach(b => b.disabled = !(!full.error && variant));
  }

  root.addEventListener('click', e => {
    const b = e.target.closest('button'); if (!b || b.disabled) return;
    if (b.matches('[data-wb-add]')) return add();
    if (b.dataset.toggle) {                                        // multi-select, unlike every other step
      const c = b.dataset.toggle;
      cfg.custom = cfg.custom.includes(c) ? cfg.custom.filter(x => x !== c) : [...cfg.custom, c];
      return render();
    }
    if (!b.dataset.set) return;
    const k = b.dataset.set, v = b.dataset.val;
    cfg[k] = (k === 'length' || k === 'bundles' || k === 'lace') ? Number(v) : v;
    if (k === 'length' && cfg.bundles && (cfg.bundles < minBundles(cfg.length) || cfg.bundles > maxBundles(cfg.length))) cfg.bundles = null;
    if (k === 'laceSize') { const lt = table[cfg.texture]?.[TYPES[cfg.type]?.lace]; if (lt && cfg.lace && !(lt.byLen[cfg.laceSize]?.[cfg.lace])) cfg.lace = null; }
    if (k === 'type' || k === 'texture') {
      const p2 = TYPES[cfg.type]?.lace; const lt = p2 ? table[cfg.texture]?.[p2] : null;
      if (!lt || (cfg.laceSize && !lt.sizes.includes(cfg.laceSize))) cfg.laceSize = null;        // old size no longer valid
      if (lt && lt.sizes.length === 1) cfg.laceSize = lt.sizes[0];                               // one size: pick it automatically
      if (!lt || !(lt.byLen[cfg.laceSize || lt.sizes[0]]?.[cfg.lace])) cfg.lace = null;
    }
    if (k === 'type') cfg.custom = customChosen(cfg);              // drop anything the new wig type cannot have
    if ((k === 'texture' || k === 'type') && cfg.length) { const e2 = probe({}).error; if (e2 && !/full look|6 bundles/.test(e2)) cfg.length = null; }
    render();   // no auto-scroll: the page stays where the customer left it
  });

  // Exposed so the "Send it to me" form (rendered by the section) can save the
  // current build into the bag without repeating the pricing and variant lookup.
  root.wbSaveBuild = function () { return add({ silent: true }); };
  // the current build's variant id + line-item properties, for the quote Worker. null if incomplete.
  root.wbQuoteData = function () {
    const r = price(cfg, table, S); if (r.error) return null;
    const id = data.index[String(r.total)]; if (!id) return null;
    return { variantId: String(id), properties: buildProps() };
  };

  // line-item properties, shared by the cart add and the quote so they never drift.
  function buildProps() {
    const t = tex(), ty = TYPES[cfg.type];
    return {
      'Texture': t.label, 'Style': ty.label,
      ...(ty.lace ? { [ty.lace === 'closure' ? 'Closure' : 'Frontal']: `${laceLabel(ty.lace)}, ${cfg.lace}"` } : {}),
      'Weft length': cfg.length + '"', 'Density': `${cfg.bundles * 100}g (${cfg.bundles} bundles)`, 'Cap size': cfg.cap,
      ...(customChosen(cfg).length ? { 'Customisation': customChosen(cfg).map(k => CUSTOM[k].label).join(', ') } : {}),
      ...(cfg.custom.includes('hairline') ? { 'Hairline photo': 'We will email you to ask for a photo of your hairline' } : {}),
    };
  }

  async function add(opts) {
    opts = opts || {};
    const r = price(cfg, table, S); if (r.error) return false;
    const id = data.index[String(r.total)]; if (!id) return false;
    const properties = buildProps();
    if (!opts.silent) root.querySelectorAll('[data-wb-add]').forEach(b => { b.disabled = true; b.textContent = 'Adding…'; });
    try {
      // Saving a design already puts it in the bag, so adding the identical build
      // again must not make it quantity 2.
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(x => x.json()).catch(() => null);
      const already = cart && (cart.items || []).some(it =>
        it.variant_id === Number(id) && JSON.stringify(it.properties || {}) === JSON.stringify(properties));
      if (!already) {
        const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(id), quantity: 1, properties }] }) });
        if (!res.ok) throw new Error((await res.json()).description || 'Could not add');
      }
      if (!opts.silent) location.href = '/cart';
      return true;
    } catch (err) {
      if (!opts.silent) {
        $('[data-wb-note]').textContent = err.message;
        root.querySelectorAll('[data-wb-add]').forEach(b => { b.disabled = false; b.textContent = 'Add to bag'; });
      }
      return false;
    }
  }
  render();
}

})();
